/**
 * Geometry for the free calculators' log-frequency magnitude charts (SBIR
 * null, comb-filter, modal seat response) — an arbitrary number of curves
 * over one log-frequency axis, each optionally mean-centered to its own 0 dB
 * average ("relative"), sharing one dB viewport that is either fixed by the
 * caller or auto-fit to the plotted data.
 *
 * This is deliberately its OWN geometry rather than a reuse of
 * `computeFreqResponseGeometry`: that function always shifts its "active"
 * curve by the offset needed to center its "baseline" (the honest before/
 * after transform for exactly two curves — see chartDraw.ts), which is a
 * different, incompatible transform from what these tools need — each curve
 * centered independently, so a flat reference curve renders as a flat 0 dB
 * line regardless of the other curve's absolute level. It also fixes the
 * curve count at two (active + optional baseline) and has no fixed-dB-range
 * override, both of which these tools need.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, type LinearScale, type LogFreqScale } from './scales';
import { mapSeriesToPlot, type PlotPoint } from './series';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import { niceLinearTicks } from './ticks';

export type ToolFreqCurveUnit = 'linear' | 'db';

export interface ToolFreqCurveInput {
  /** Magnitude (linear) or dB, per `unit`. Parallel to the shared `freqs`. */
  values: number[];
  unit?: ToolFreqCurveUnit;
}

export interface ToolFreqInput {
  freqs: number[];
  curves: ToolFreqCurveInput[];
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  /** Mean-center each curve to its own ~0 dB average — shows resonance SHAPE
   *  independent of absolute level (a flat reference curve becomes a flat
   *  line at 0). Ignored for a curve already in dB. Default false. */
  relative?: boolean;
  /** Fixed vertical window (dB). Omit to auto-fit to the plotted curves,
   *  seeded at [-24, 6] and only ever expanded to fit the data (never
   *  shrunk below that seed). */
  dbRange?: { min: number; max: number };
  measureWidth?: (label: string) => number;
}

export interface ToolFreqCurveGeometry {
  /** The curve's dB values, post mean-centering (or passthrough for `unit: 'db'`). */
  db: number[];
  points: PlotPoint[];
}

export interface ToolFreqGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  yDomain: { lo: number; hi: number };
  xTicks: AxisTicks;
  yTicks: YTick[];
  /** Same order as `opts.curves`. */
  curves: ToolFreqCurveGeometry[];
}

const DB_SEED_MIN = -24;
const DB_SEED_MAX = 6;

function toDb(v: number): number {
  return 20 * Math.log10(Math.max(v, 1e-9));
}

/** Mirrors the original ToolFreqChart's per-curve transform exactly: a `db`
 *  unit curve passes through untouched (relative is meaningless for data
 *  that is already a level a caller chose deliberately, e.g. a fixed
 *  example), a `linear` curve converts to dB and, when `relative`, is then
 *  shifted so ITS OWN mean lands at 0 — independently of any other curve. */
function curveToDb(values: number[], unit: ToolFreqCurveUnit, relative: boolean): number[] {
  if (unit === 'db') return values.slice();
  const db = values.map(toDb);
  if (!relative) return db;
  const mean = db.reduce((a, b) => a + b, 0) / Math.max(1, db.length);
  return db.map((v) => v - mean);
}

export function computeToolFreqGeometry(opts: ToolFreqInput): ToolFreqGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 300;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const emptyYDomain = { lo: opts.dbRange?.min ?? DB_SEED_MIN, hi: opts.dbRange?.max ?? DB_SEED_MAX };
  if (!opts.freqs.length || !opts.curves.length || frame.plotW <= 0) {
    return {
      empty: true,
      frame,
      fMin,
      fMax,
      xScale,
      yScale: createLinearScale(frame.plotH, emptyYDomain.lo, emptyYDomain.hi, true),
      yDomain: emptyYDomain,
      xTicks,
      yTicks: [],
      curves: [],
    };
  }

  const dbCurves = opts.curves.map((c) => curveToDb(c.values, c.unit ?? 'linear', opts.relative ?? false));

  let dbMin = opts.dbRange?.min ?? DB_SEED_MIN;
  let dbMax = opts.dbRange?.max ?? DB_SEED_MAX;
  if (!opts.dbRange) {
    for (const db of dbCurves) {
      for (const d of db) {
        if (!Number.isFinite(d)) continue;
        if (d < dbMin) dbMin = d;
        if (d > dbMax) dbMax = d;
      }
    }
    // Rounded outward to a 6 dB grid, with a little headroom above the peak —
    // the exact rule the bespoke tool chart used, kept so an unpinned axis
    // doesn't visually jump for the tools that migrate onto this geometry.
    dbMin = Math.floor(dbMin / 6) * 6 - 6;
    dbMax = Math.ceil(dbMax / 6) * 6 + 3;
  }

  const yScale = createLinearScale(frame.plotH, dbMin, dbMax, true);
  const { values: yTickVals, majorInterval } = niceLinearTicks(dbMin, dbMax, 8);
  const yTicks: YTick[] = yTickVals.map((v) => ({
    value: v,
    y: linearToPlot(yScale, v),
    major: Math.round(v) % majorInterval === 0,
    label: `${Math.round(v)}`,
  }));

  const curves: ToolFreqCurveGeometry[] = dbCurves.map((db) => ({
    db,
    points: mapSeriesToPlot({ x: opts.freqs, y: db }, xScale, yScale),
  }));

  return {
    empty: false,
    frame,
    fMin,
    fMax,
    xScale,
    yScale,
    yDomain: { lo: dbMin, hi: dbMax },
    xTicks,
    yTicks,
    curves,
  };
}
